import React from 'react';
import { motion, AnimatePresence, PanInfo } from 'framer-motion';
import { X } from 'lucide-react';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';
import { useMobile } from '../../hooks/use-mobile';

interface BottomSheetProps {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  title?: string;
  description?: string;
  className?: string;
  showHandle?: boolean;
  snapPoints?: number[];
  size?: 'normal' | 'wide';
  contentClassName?: string;
}

export function BottomSheet({
  open,
  onClose,
  children,
  title,
  description,
  className,
  showHandle = true,
  snapPoints = [1],
  size = 'normal',
  contentClassName,
}: BottomSheetProps) {
  const isMobile = useMobile();
  const [currentSnap] = React.useState(() => Math.max(0, snapPoints.length - 1));

  const handleDragEnd = (_: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
    const shouldClose = info.velocity.y > 500 || info.offset.y > 100;
    if (shouldClose) {
      onClose();
    }
  };

  // On desktop, render as a centered modal
  if (!isMobile) {
    return (
      <AnimatePresence>
        {open && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={onClose}
              className="fixed inset-0 z-[70] bg-black/50"
            />
            {/* Modal - Positioning wrapper */}
            <div className="fixed left-1/2 top-1/2 z-[70] -translate-x-1/2 -translate-y-1/2">
              {/* Animation wrapper */}
              <motion.div
                role="dialog"
                aria-modal="true"
                aria-labelledby={title ? 'bottom-sheet-title' : undefined}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className={cn(
                  'w-[calc(100vw-2rem)] max-h-[90vh] rounded-lg bg-background shadow-lg flex flex-col overflow-hidden border',
                  size === 'wide' ? 'max-w-5xl' : 'max-w-2xl',
                  className
                )}
              >
              {/* Header - fixed */}
              <div className="flex-shrink-0 p-6 pb-4 border-b">
                {(title || description) && (
                  <div>
                    {title && <h2 id="bottom-sheet-title" className="text-lg font-semibold pr-8">{title}</h2>}
                    {description && (
                      <p className="text-sm text-muted-foreground mt-1">{description}</p>
                    )}
                  </div>
                )}
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute right-4 top-4"
                  onClick={onClose}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>

              {/* Content - scrollable */}
              <div
                className={cn(
                  'flex-1 min-h-0 overflow-y-auto px-6 py-4 scrollbar-thin scrollbar-thumb-muted scrollbar-track-transparent',
                  contentClassName,
                )}
                style={{ overflowY: 'auto' }}
              >
                {children}
              </div>
              </motion.div>
            </div>
          </>
        )}
      </AnimatePresence>
    );
  }

  // Mobile bottom sheet
  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-50 bg-black/50"
          />
          {/* Sheet */}
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-labelledby={title ? 'bottom-sheet-title-mobile' : undefined}
            initial={{ y: '100%' }}
            animate={{ y: `${(1 - snapPoints[currentSnap]) * 100}%` }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            drag="y"
            dragConstraints={{ top: 0, bottom: 0 }}
            dragElastic={{ top: 0, bottom: 0.5 }}
            onDragEnd={handleDragEnd}
            className={cn(
              'fixed inset-x-0 bottom-0 z-[70] flex flex-col rounded-t-2xl bg-background shadow-2xl',
              className
            )}
            style={{ maxHeight: '95dvh' }}
          >
            {/* Handle */}
            {showHandle && (
              <div className="flex justify-center pt-3 pb-2">
                <div className="h-1 w-12 rounded-full bg-muted-foreground/30" />
              </div>
            )}

            {/* Header */}
            {(title || description) && (
              <div className="px-4 pb-4 border-b">
                {title && <h2 id="bottom-sheet-title-mobile" className="text-lg font-semibold">{title}</h2>}
                {description && (
                  <p className="text-sm text-muted-foreground mt-1">{description}</p>
                )}
              </div>
            )}

            {/* Content */}
            <div
              className={cn(
                'flex-1 overflow-y-auto p-4 pb-[calc(env(safe-area-inset-bottom)+1rem)]',
                contentClassName,
              )}
            >
              {children}
            </div>

            {/* Safe area spacer for devices with home indicator */}
            <div className="h-[env(safe-area-inset-bottom)]" />
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

export default BottomSheet;
